/* eslint max-statements: ["error", {max: 30}] -- applies to entire file */
/* eslint complexity: ["error", {max: 7}] -- applies to entire file */
import { useEffect } from "react";

import { format } from "date-fns";
import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
  ProfileAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { DateFormat } from "@/constants/DateFormat";
import { ModalPath } from "@/constants/Navigation";
import { ModalTestID } from "@/constants/TestID";
import { router, useLocalSearchParams } from "@/crossPlatform/expo-router";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetSafetyPlanDetailsQuery } from "@/store/api/user";
import AdditionalHelpfulInfo from "@/ui/components/profile/safetyPlan/AdditionalHelpfulInfo";
import PersonalSafetyPlan from "@/ui/components/profile/safetyPlan/PersonalSafetyPlan";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { FAIconName, FullScreenSpinner, IconButton } from "@/ui/core";
import { VStack } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import { shareSafetyPlan } from "@/utils/profile/safetyplan.util";
import { joinTestIDs } from "@/utils/string.util";

const getCompletedOn = (endDate?: string) => {
  if (isNil(endDate)) return "";
  const formattedEndDate = format(
    new Date(endDate),
    DateFormat.shortMonthDayYear,
  );
  return `Completed on ${formattedEndDate}`;
};

const Modal = () => {
  const { showToast } = useToast();
  const { captureException } = useAppMonitor();
  const { track, trackPageView } = useAnalytics();
  useEffect(() => {
    trackPageView(ProfileAnalyticsPageView.SafetyPlan);
  }, [trackPageView]);

  const { firstName, selectedUserId } = useLocalSearchParams<{
    firstName: string;
    selectedUserId: string;
  }>();
  const {
    data: safetyPlanData,
    error: safetyPlanError,
    isError: isSafetyPlanError,
    isLoading: isSafetyPlanLoading,
  } = useGetSafetyPlanDetailsQuery(selectedUserId);
  // if have no data, a spinner is displayed
  // if data is re-fetching, we ignore this and proceed with rendering modal
  // this is because displaying slightly stale data on this modal is acceptable
  // as this modal does not involve any updates or possible mutation to that data
  if (isSafetyPlanLoading) {
    return <FullScreenSpinner />;
  }
  // cannot display safety plan if error getting full safety plan
  const errorToastText =
    "There was an issue trying to retrieve this safety plan's details";
  if (isSafetyPlanError || !isNil(safetyPlanError)) {
    captureException(safetyPlanError);
    showToast({ text: errorToastText, variant: "error" });
    router.dismiss();
    return <FullScreenSpinner />;
  }
  if (isNil(safetyPlanData)) {
    captureException(
      new Error(
        "Fetched user's safety plan details from API but received empty data",
      ),
    );
    showToast({ text: errorToastText, variant: "error" });
    router.dismiss();
    return <FullScreenSpinner />;
  }

  const { safetyPlan, surveyResponseDetails } = safetyPlanData;
  const {
    copingSkills: copingSkillsData,
    imminentRiskPlan: imminentRiskOfSelfHarm,
    localEmsPhone,
    otherWarningSigns,
    reasonToLive: mostImportantReasons,
    supportPeople,
    warningSigns: warningSignsData,
  } = safetyPlan;

  const title = `${firstName}'s safety plan`;
  const completedOn = getCompletedOn(surveyResponseDetails.endDate);
  const warningSigns = isNil(warningSignsData)
    ? []
    : Object.values(warningSignsData);

  const copingSkills = copingSkillsData?.map(
    (copingSkill: SafetyPlanCopingSkill) =>
      `${copingSkill.Skill} - ${copingSkill.Description}`,
  );

  const handleDismiss = () => {
    track(ProfileAnalyticsEvent.DismissSafetyPlan, {
      [AnalyticsFieldName.Component]: ModalPath.SafetyPlanDetails,
      [AnalyticsFieldName.Source]: ModalPath.SafetyPlanDetails,
    });
    router.dismiss();
  };

  const handleShareSafetyPlan = () => {
    track(ProfileAnalyticsEvent.ShareSafetyPlanPressed, {
      [AnalyticsFieldName.Component]: ModalPath.SafetyPlanDetails,
      [AnalyticsFieldName.SelectedUser]: selectedUserId,
      [AnalyticsFieldName.Source]: ModalPath.SafetyPlanDetails,
    });
    void shareSafetyPlan({ completedOn, safetyPlan, title });
  };

  return (
    <ModalLayout
      isPaddedHorizontally
      renderHeader={() => {
        return (
          <ModalTitleHeader
            actionComponents={
              <IconButton
                iconName={FAIconName.ShareFromSquare}
                onPress={handleShareSafetyPlan}
                testID={joinTestIDs(
                  ModalTestID.TitleHeader,
                  ModalTestID.SafetyPlanDetails,
                  "share",
                )}
              />
            }
            navigationType="back"
            onDismiss={handleDismiss}
            testID={joinTestIDs(
              ModalTestID.TitleHeader,
              ModalTestID.SafetyPlanDetails,
            )}
            title="Safety plan"
          />
        );
      }}
    >
      <VStack gap={36} paddingBottom={36} paddingTop={24}>
        <PersonalSafetyPlan
          completedOn={completedOn}
          copingSkills={copingSkills}
          imminentRiskOfSelfHarm={imminentRiskOfSelfHarm}
          localEmsPhone={localEmsPhone}
          mostImportantReasons={mostImportantReasons}
          otherWarningSigns={otherWarningSigns}
          selectedUserId={selectedUserId}
          supportPeople={supportPeople}
          title={title}
          warningSigns={warningSigns}
        />
        <AdditionalHelpfulInfo selectedUserId={selectedUserId} />
      </VStack>
    </ModalLayout>
  );
};

export default Modal;
